import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { compose } from 'redux';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { JsonLdScript } from 'components/atoms';
import { get, pickBy, isEqual } from 'lodash';
import { useCookies } from 'react-cookie';
import Helmet from 'react-helmet';

import { scrollToElement } from '@sparefoot/react-core/utils/scrolling';

import { metaPropTypes, phonePropType } from 'config/propTypes';
import { isLarge } from 'utils/breakpoints';

import ErrorPage from 'pages/ErrorPage/ErrorPage';
import { H2 } from 'components/core/Typography';
import { LoadingPage } from 'components/core/LoadingPage';
import { LazyHydrate } from 'components/core/LazyHydrate';
import { Page } from 'components/core/Page';
import { SearchContent } from 'components/search/SearchContent';
import { SearchResults } from 'components/search/SearchResults';
import { PaginationSummary } from 'components/search/PaginationSummary';
import { SearchPagination } from 'components/search/SearchPagination';
// Legacy SearchToolBar replaced locally on NewSearchPage with redesigned NewSearchToolbar (normalized casing)
import NewSearchToolbar from 'components/search/NewSearchToolbar';
import SearchFiltersTray from 'components/search/SearchFiltersTray';
import SearchPageFooter from 'components/search/SearchPageFooter';
import { SearchMap } from 'components/search/SearchMap';
import { NewSortDropDown } from 'components/search/NewSortDropDown';

import searchSelector, { getDealsFilter } from 'store/selectors/search';
import {
	getSearchResults as getSearchResultsAction,
	getSearchLandingPage as getSearchLandingPageAction,
	updateSearchFilters as updateSearchFiltersAction,
} from 'store/modules/search';

import { newDefaultUnitFilters, newSortOptions } from 'config/filters';
import {
	buildSearchLink,
	parseSearchLink,
	countAppliedFilters,
} from 'utils/search';
import DealsFilter from 'components/search/DealsFilter';

import './NewSearchPage.scss';
import { PAGE_TYPES, PAGE_SUB_TYPES } from '@sparefoot/tracking-events';
import {
	INTERNAL_PAGE_TYPES,
	STICKY_TOOLBAR_SCROLL_OFFSET,
} from '../../config/constants';
import { getKeywordConfig } from 'config/nearMeKeywords';
import { withExperiment } from '@sparefoot/react-core/decorators';
import { toTitleCase } from '../../utils/formatting';
import usePageViewTracking from 'hooks/usePageViewTracking';
import useSearchHandler from 'hooks/useSearchHandler';
import usePrevious from 'hooks/usePrevious';
import useViewportWidth from 'hooks/useViewportWidth';
import { mapAmenitiesToFeature } from 'config/amenityMap';

const SCHEMA_DAY_MAP = {
	monday: 'Monday',
	tuesday: 'Tuesday',
	wednesday: 'Wednesday',
	thursday: 'Thursday',
	friday: 'Friday',
	saturday: 'Saturday',
	sunday: 'Sunday',
};

export function buildOpeningHoursSpecification(hours) {
	if (!hours?.office) return undefined;

	const grouped = {};
	Object.entries(hours.office).forEach(([day, hour]) => {
		if (
			hour?.open &&
			hour?.closed &&
			typeof hour.open === 'string' &&
			typeof hour.closed === 'string' &&
			hour.open !== '-' &&
			hour.closed !== '-'
		) {
			const mappedDay = SCHEMA_DAY_MAP[day.toLowerCase()];
			if (!mappedDay) return;
			const key = `${hour.open.slice(0, 5)}_${hour.closed.slice(0, 5)}`;
			if (!grouped[key]) {
				grouped[key] = {
					opens: hour.open.slice(0, 5),
					closes: hour.closed.slice(0, 5),
					days: [],
				};
			}
			grouped[key].days.push(mappedDay);
		}
	});

	const specs = Object.values(grouped).map(({ opens, closes, days }) => ({
		'@type': 'OpeningHoursSpecification',
		dayOfWeek: days,
		opens,
		closes,
	}));
	return specs.length ? specs : undefined;
}

export function buildFacilitySchemaFields(facility, cloudfrontUrl, baseUrl) {
	const loc = facility.location || {};
	const streetAddress = [loc.streetAddress1, loc.streetAddress2]
		.filter(Boolean)
		.join(', ');

	const fields = {
		name: facility.name,
		url: facility.url ? `${baseUrl}${facility.url}` : undefined,
		address: {
			'@type': 'PostalAddress',
			streetAddress: streetAddress || undefined,
			addressLocality: loc.city,
			addressRegion: loc.state,
			postalCode: loc.zip,
			addressCountry: 'US',
		},
	};

	if (loc.latitude && loc.longitude) {
		fields.geo = {
			'@type': 'GeoCoordinates',
			latitude: loc.latitude,
			longitude: loc.longitude,
		};
	}

	if (facility.facilityPhone) {
		fields.telephone = facility.facilityPhone;
	}

	if (facility.reviews?.average && facility.reviews?.number) {
		fields.aggregateRating = {
			'@type': 'AggregateRating',
			ratingValue: facility.reviews.average,
			reviewCount: facility.reviews.number,
		};
	}

	fields.priceRange = '$-$$';

	const paymentMethodsMap = {
		cashAccepted: 'Cash',
		creditCardsAccepted: 'Credit Card',
		checksAccepted: 'Check',
	};
	if (
		facility.billingAmenities &&
		typeof facility.billingAmenities === 'object'
	) {
		const paymentMethods = Object.entries(facility.billingAmenities)
			.filter(
				([key, item]) =>
					item?.hasAttribute === true && paymentMethodsMap[key],
			)
			.map(([key]) => paymentMethodsMap[key]);
		if (paymentMethods.length) fields.paymentAccepted = paymentMethods;
	}

	fields.currenciesAccepted = 'USD';

	const { smallUnit, mediumUnit, largeUnit } = facility.units || {};
	const schemaPromotion =
		smallUnit?.special || mediumUnit?.special || largeUnit?.special || null;
	if (schemaPromotion) {
		const priceMatches = schemaPromotion.includes('$')
			? schemaPromotion.match(/\$[\d,]+(?:\.\d{2})?/g)
			: null;
		const extractedPrice = priceMatches?.length
			? priceMatches[0].replace('$', '').replace(/,/g, '')
			: null;
		fields.makesOffer = {
			'@type': 'Offer',
			name: schemaPromotion,
			description: schemaPromotion,
			...(extractedPrice && {
				price: extractedPrice,
				priceCurrency: 'USD',
			}),
			availability: 'https://schema.org/InStock',
		};
	}

	const amenityFeature = mapAmenitiesToFeature(facility);
	if (amenityFeature && amenityFeature.length) {
		fields.amenityFeature = amenityFeature;
	}

	const openingHoursSpecification = buildOpeningHoursSpecification(
		facility.hours,
	);
	if (openingHoursSpecification) {
		fields.openingHoursSpecification = openingHoursSpecification;
	}

	const images = facility.images || [];
	if (images.length && cloudfrontUrl) {
		fields.image = images.map((img) => ({
			'@type': 'ImageObject',
			url: `https:${cloudfrontUrl}/medium-compress/${img}`,
		}));
	}

	if (facility.staticMapUrl) {
		fields.hasMap = facility.staticMapUrl;
	}

	return fields;
}

export function NewSearchPage(props) {
	const dispatch = useDispatch();
	const [cookiesObj, setCookie] = useCookies();
	const safeCookies = useMemo(
		() => ({
			get: (name) => cookiesObj[name],
			set: (name, value, options) => setCookie(name, value, options),
		}),
		[cookiesObj, setCookie],
	);
	const selectorProps = useMemo(
		() => ({ cookies: safeCookies }),
		[safeCookies],
	);
	const storeProps = useSelector((state) =>
		searchSelector(state, selectorProps),
	);
	const location = useLocation();
	const navigate = useNavigate();
	const { viewportWidth: hookViewportWidth } = useViewportWidth();

	const {
		errorMessage = storeProps.errorMessage,
		facilities = storeProps.facilities,
		baseUrl = storeProps.baseUrl,
		faqContent = storeProps.faqContent,
		faqShowing = storeProps.faqShowing,
		listingsPerPage = storeProps.listingsPerPage,
		loading = storeProps.loading,
		meta = storeProps.meta,
		page = storeProps.page,
		pageType = storeProps.pageType,
		phone,
		searchContent = storeProps.searchContent,
		searchLocation = storeProps.searchLocation,
		searchParams = storeProps.searchParams,
		searchType = storeProps.searchType,
		showInformedSearch = storeProps.showInformedSearch,
		showRecommendedUnit = storeProps.showRecommendedUnit,
		totalListings = storeProps.totalListings,
		totalPages = storeProps.totalPages,
		trackingPixel = storeProps.trackingPixel,
		handleSearch: handleSearchProp,
		facetCounts = storeProps.facetCounts,
		storageTypeUrls = storeProps.storageTypeUrls,
		sf16978Variation,
		withinCallCenterHours = storeProps.withinCallCenterHours,
		staticMapUrl = storeProps.staticMapUrl,
		cloudfrontUrl = storeProps.cloudfrontUrl,
		useNewSearch = storeProps.useNewSearch,
		stateUrl = storeProps.stateUrl,
		cityUrl = storeProps.cityUrl,
		searchId = storeProps.searchId,
		statusCode = storeProps.statusCode,
		headline = storeProps.headline,
		drillDownLinks = storeProps.drillDownLinks,
		viewportWidth = hookViewportWidth,
	} = props;

	// Inlined withSearchHandler
	const searchHandler = useSearchHandler({});
	const handleSearch = handleSearchProp ?? searchHandler;

	// Inlined withSearchPageHandlers
	const prevLocationSearch = usePrevious(location.search);
	const prevLoading = usePrevious(loading);
	const prevPage = usePrevious(page);

	const onSearch = useCallback(
		(filterValues, delayFiltering = false) => {
			// page: 1 must come AFTER filterValues so pagination resets when
			// filters change (SF-19283).
			const params = pickBy({
				...searchParams,
				...filterValues,
				page: 1,
			});
			dispatch(updateSearchFiltersAction(params));
			if (!delayFiltering) {
				navigate({ search: buildSearchLink(params) });
			}
		},
		[searchParams, dispatch, navigate],
	);

	useEffect(() => {
		if (location.search && !isEqual(location.search, prevLocationSearch)) {
			const query = parseSearchLink(location.search);
			// SF-19130: storage landing pages must use getSearchLandingPage
			// so content (e.g. dynamic headlines) is refreshed. Vehicle
			// searches must use getSearchResults because the landing endpoint
			// doesn't handle vehicle filtering.
			const {
				pageType: queryPageType,
				type,
				searchType: querySearchType,
			} = query || {};
			const isVehicleSearch =
				type === 'vehicle' || querySearchType === 'vehicle';
			const isStorageLandingPage =
				!isVehicleSearch &&
				(queryPageType === INTERNAL_PAGE_TYPES.CITY_LANDING_PAGE ||
					queryPageType === INTERNAL_PAGE_TYPES.AMENITY_LANDING_PAGE);

			if (isStorageLandingPage) {
				dispatch(getSearchLandingPageAction(query, location.key));
			} else {
				dispatch(getSearchResultsAction(query, location.key));
			}
		}
		if (prevLoading && !loading && page !== prevPage) {
			scrollToElement('#search-page-results', { offset: -16 });
		}
	});

	usePageViewTracking(
		props.pageType === INTERNAL_PAGE_TYPES.NEAR_ME_SEARCH_PAGE
			? PAGE_SUB_TYPES.STORAGE_NEAR_ME_LANDING
			: PAGE_TYPES.SEARCH,
	);

	const dealsFilter = useSelector(getDealsFilter);

	// --- withStateHandlers ---
	const [facilitySelected, setFacilitySelected] = useState(null);
	const [viewListMap, setViewListMap] = useState(true);
	const [filtersShowing, setFiltersShowing] = useState(false);
	const [defaultFilters] = useState(newDefaultUnitFilters);
	const [pinSelected, setPinSelected] = useState(false);

	const handleFacilitySelected = useCallback(
		(facilityId) => setFacilitySelected(facilityId),
		[],
	);
	const handleFacilityDeselected = useCallback(
		() => setFacilitySelected(null),
		[],
	);
	const handlePinSelected = useCallback(() => setPinSelected(true), []);
	const handlePinDeselected = useCallback(() => setPinSelected(false), []);
	const handleViewListMap = useCallback(() => {
		scrollToElement('#search-page-results', {
			offset: -STICKY_TOOLBAR_SCROLL_OFFSET,
		});
		setViewListMap((prev) => !prev);
	}, []);
	const toggleFilters = useCallback(
		() => setFiltersShowing((prev) => !prev),
		[],
	);
	const closeFilters = useCallback(() => setFiltersShowing(false), []);

	// Compute applied filters count directly from search params (SF-18689)
	const filtersCount = countAppliedFilters(searchParams);

	// Inlined withErrorHandler
	if (statusCode > 200) {
		return <ErrorPage statusCode={statusCode} />;
	}

	// --- branch: loading gate ---
	if (loading && !searchId) {
		return <LoadingPage />;
	}

	const {
		city,
		state: stateAbbreviation,
		subPageType,
		vehicle,
	} = searchParams;
	const titleCaseCity = city ? toTitleCase(city) : '';
	const storageTypeUrlsSafe =
		pageType == INTERNAL_PAGE_TYPES.VEHICLE_LANDING_PAGE
			? storageTypeUrls?.vehicle
			: storageTypeUrls?.storage;
	const landingPageUrl = `${baseUrl}${storageTypeUrlsSafe}${subPageType || ''}`;

	const getAmenityType = () => {
		if (pageType === INTERNAL_PAGE_TYPES.CITY_LANDING_PAGE && city)
			return '';
		if (
			pageType === INTERNAL_PAGE_TYPES.AMENITY_LANDING_PAGE &&
			subPageType
		)
			return subPageType;
		if (pageType === INTERNAL_PAGE_TYPES.VEHICLE_LANDING_PAGE && vehicle)
			return vehicle;
		return '';
	};
	const amenityType = getAmenityType();

	const shouldRenderSchema =
		(pageType === 'cityLandingPage' ||
			pageType === 'amenityLandingPage' ||
			pageType === 'vehicleLandingPage' ||
			pageType === 'nearMeSearchPage') &&
		page == 1;

	const isNMP = pageType === INTERNAL_PAGE_TYPES.NEAR_ME_SEARCH_PAGE;
	const nmpKeywordConfig = isNMP
		? getKeywordConfig(searchParams.subPageType)
		: null;
	const isDealsPage = searchParams.subPageType === 'deals';
	const handleDealsFilterChange = (filterValue) => {
		onSearch({ ...searchParams, page: 1, dealsFilter: filterValue });
	};

	// Scroll selected facility card into view (desktop variant only)
	const scrollToFacilityCard = (facilityId) => {
		if (!facilityId) return;
		requestAnimationFrame(() => {
			scrollToElement(`#facility-card-${facilityId}`, { offset: -100 });
		});
	};

	return (
		<Page
			className={classnames('search-page sf-16978-ssdc-clp-redesign', {
				'near-me-page': isNMP,
			})}
			trackingPixel={trackingPixel}
			loading={loading}
			pageType={pageType}
			title={meta.title}
			description={meta.description}
			canonical={meta.canonical}
			noIndex={meta.noindex}
			noFollow={meta.nofollow}
			prev={meta.prev}
			next={meta.next}
		>
			{(pageType === INTERNAL_PAGE_TYPES.CITY_LANDING_PAGE ||
				pageType === INTERNAL_PAGE_TYPES.AMENITY_LANDING_PAGE ||
				pageType === INTERNAL_PAGE_TYPES.VEHICLE_LANDING_PAGE) && (
				<>
					<JsonLdScript
						schema={{
							'@context': 'https://schema.org',
							'@type': 'WebSite',
							name: 'SelfStorage.com',
							url: 'https://www.selfstorage.com',
							potentialAction: {
								'@type': 'SearchAction',
								target: {
									'@type': 'EntryPoint',
									urlTemplate: `https://www.selfstorage.com/self-storage/${stateAbbreviation}/${city}`,
									inLanguage: 'en-US',
									actionPlatform: [
										'http://schema.org/DesktopWebPlatform',
										'http://schema.org/MobileWebPlatform',
									],
								},
								'query-input': {
									'@type': 'PropertyValueSpecification',
									valueRequired: 'http://schema.org/False',
									valueName: 'location',
								},
							},
						}}
					/>
					<JsonLdScript
						schema={{
							'@context': 'https://schema.org',
							'@type': 'WebPage',
							name: `${titleCaseCity} Self Storage`,
							url: landingPageUrl,
							description: meta.description,
							mainEntity: {
								'@type': 'Place',
								name: `${titleCaseCity}, ${searchLocation?.state}`,
								hasMap: {
									'@type': 'Map',
									name: `${titleCaseCity} Self Storage Map`,
									url: staticMapUrl,
								},
							},
						}}
					/>
				</>
			)}

			{isNMP && page === 1 && nmpKeywordConfig && (
				<Helmet>
					<script type="application/ld+json">
						{JSON.stringify({
							'@context': 'https://schema.org',
							'@graph': [
								{
									'@type': 'WebSite',
									name: 'http://SelfStorage.com',
									url: 'https://www.selfstorage.com/',
									potentialAction: {
										'@type': 'SearchAction',
										target: {
											'@type': 'EntryPoint',
											urlTemplate:
												'https://www.selfstorage.com/search?location={search_term_string}',
											inLanguage: 'en-US',
											actionPlatform: [
												'http://schema.org/DesktopWebPlatform',
												'http://schema.org/MobileWebPlatform',
											],
										},
										'query-input': {
											'@type':
												'PropertyValueSpecification',
											valueRequired:
												'http://schema.org/True',
											valueName: 'search_term_string',
										},
									},
								},
								{
									'@type': 'BreadcrumbList',
									itemListElement: [
										{
											'@type': 'ListItem',
											position: 1,
											item: {
												'@type': 'WebSite',
												'@id': 'https://www.selfstorage.com/',
												name: 'Home',
												url: 'https://www.selfstorage.com/',
											},
										},
										{
											'@type': 'ListItem',
											position: 2,
											item: {
												'@type': 'WebPage',
												'@id': `https://www.selfstorage.com/self-storage-near-me/${searchParams.subPageType}/`,
												name:
													headline ||
													'Self Storage Near Me',
												url: `https://www.selfstorage.com/self-storage-near-me/${searchParams.subPageType}/`,
											},
										},
									],
								},
								{
									'@type': 'WebPage',
									name: meta.title,
									url: meta.canonical,
									description: meta.description,
									mainEntity: {
										'@type': 'ItemList',
										numberOfItems: facilities.length,
									},
								},
								...facilities.map((facility) => ({
									'@type': 'SelfStorage',
									...buildFacilitySchemaFields(
										facility,
										cloudfrontUrl,
										baseUrl,
									),
								})),
								...(faqContent && faqContent.length > 0
									? [
											{
												'@type': 'FAQPage',
												mainEntity: faqContent.map(
													(faq) => ({
														'@type': 'Question',
														name: faq.title,
														acceptedAnswer: {
															'@type': 'Answer',
															text: faq.copy,
														},
													}),
												),
											},
										]
									: []),
							],
						})}
					</script>
				</Helmet>
			)}

			<NewSearchToolbar
				withinCallCenterHours={withinCallCenterHours}
				onSearch={handleSearch}
				location={searchLocation}
				onOpenFilters={toggleFilters}
				filtersCount={filtersCount}
				headline={headline}
				pageType={pageType}
				loading={loading}
				className={isNMP ? 'nmp-toolbar' : undefined}
				sticky
				// Filter tray props - desktop dropdown rendered inside toolbar for proper positioning
				filtersShowing={filtersShowing}
				closeFilters={closeFilters}
				searchParams={searchParams}
				onFilter={onSearch}
				searchType={searchType}
				unitFilters={defaultFilters}
				facetCounts={facetCounts}
				useNewSearch={useNewSearch}
			/>

			{/* Mobile filter tray rendered outside sticky toolbar to avoid
			    stacking context trap.
			    Only rendered on mobile viewports — desktop uses the dropdown
			    inside NewSearchToolbar instead.
				Prevents SSR issues with filtersShowing defaulting to false,
				only true on user interaction.
				Matches existing pattern on SFDC & in this codebase.
				*/}
			{!isLarge(viewportWidth) && filtersShowing && (
				<SearchFiltersTray
					filtersShowing={filtersShowing}
					closeFilters={closeFilters}
					searchParams={searchParams}
					onFilter={onSearch}
					searchType={searchType}
					unitFilters={defaultFilters}
					facetCounts={facetCounts}
					useNewSearch={useNewSearch}
					className="mobile-filter-tray"
					idPrefix="mobile-"
				/>
			)}

			<SearchPageFooter
				withinCallCenterHours={withinCallCenterHours}
				viewListMap={viewListMap}
				handleViewListMap={handleViewListMap}
				onToggleListMap={handleViewListMap}
				onOpenFilters={toggleFilters}
				filtersCount={filtersCount}
				sticky
			/>

			<LazyHydrate
				id="lazy-search-page"
				noLazy={!showInformedSearch}
			>
				<div className="search-results-container">
					<SearchMap
						className={classnames({
							'display-view-list-map': viewListMap,
						})}
						facilities={facilities}
						baseUrl={baseUrl}
						facilitySelected={facilitySelected}
						handleFacilityDeselected={handleFacilityDeselected}
						handleFacilitySelected={handleFacilitySelected}
						handlePinDeselected={handlePinDeselected}
						handlePinSelected={handlePinSelected}
						handleSearch={handleSearch}
						location={searchLocation}
						mapShowing
						pinSelected={pinSelected}
						searchType={searchType}
						searchParams={searchParams}
						errorMessage={errorMessage}
						staticMapUrl={staticMapUrl}
						cloudfrontUrl={cloudfrontUrl}
						isNewSearchVariant={sf16978Variation === 'variation'}
						onScrollToFacilityCard={scrollToFacilityCard}
					/>
					<div
						id="search-page-results"
						className={classnames('results-container', {
							'display-view-list-map': !viewListMap,
						})}
					>
						{isDealsPage && (
							<div className="deals-filter-row">
								<DealsFilter
									value={dealsFilter}
									onChange={handleDealsFilterChange}
								/>
							</div>
						)}
						<div className="results-n-pagination">
							{searchLocation && (
								<div className="results-header">
									<H2
										size="smallMedium"
										weight="standard"
										color="darkGray"
										className="title-results-pagination"
										last
									>
										<PaginationSummary
											listingsPerPage={listingsPerPage}
											pageListingsCount={
												facilities.length
											}
											page={page}
											totalItems={totalListings}
											amenityType={amenityType}
											city={searchLocation.city}
											state={searchLocation.state}
											pageType={pageType}
											subPageType={subPageType}
										/>
									</H2>
									<NewSortDropDown
										onChange={onSearch}
										value={searchParams?.sort}
										options={newSortOptions}
									/>
								</div>
							)}
							<SearchResults
								error={errorMessage}
								facilities={facilities}
								baseUrl={baseUrl}
								facilitySelected={facilitySelected}
								handleFacilityDeselected={
									handleFacilityDeselected
								}
								handleFacilitySelected={handleFacilitySelected}
								handlePinDeselected={handlePinDeselected}
								handlePinSelected={handlePinSelected}
								isCLP={shouldRenderSchema && !isNMP}
								isDealsPage={isDealsPage}
								loading={loading}
								location={searchLocation}
								phone={phone}
								searchType={searchType}
								showRecommendedUnit={showRecommendedUnit}
								searchParams={searchParams}
								isNewSearchPage={
									sf16978Variation === 'variation'
								}
								cloudfrontUrl={cloudfrontUrl}
							/>
							<SearchPagination
								numPages={totalPages}
								searchParams={searchParams}
								selectedPage={page}
							/>
						</div>
						{searchContent &&
							searchLocation &&
							(pageType ===
								INTERNAL_PAGE_TYPES.CITY_LANDING_PAGE ||
								pageType ===
									INTERNAL_PAGE_TYPES.AMENITY_LANDING_PAGE ||
								pageType ===
									INTERNAL_PAGE_TYPES.VEHICLE_LANDING_PAGE ||
								pageType ===
									INTERNAL_PAGE_TYPES.NEAR_ME_SEARCH_PAGE) && (
								<SearchContent
									pageType={pageType}
									location={searchLocation}
									content={searchContent}
									searchParams={searchParams}
									faqContent={faqContent}
									headline={headline}
									faqShowing={faqShowing}
									amenityType={amenityType}
									cityUrl={cityUrl}
									stateUrl={stateUrl}
									baseUrl={baseUrl}
									searchType={searchType}
									headline={headline}
									averageMonthlyUnitPrice={get(
										searchContent,
										'averageMonthlyUnitPrice',
									)}
									subPageType={searchParams.subPageType}
									drillDownLinks={drillDownLinks}
								/>
							)}
					</div>
				</div>
			</LazyHydrate>
		</Page>
	);
}

NewSearchPage.propTypes = {
	drillDownLinks: PropTypes.object,
	errorMessage: PropTypes.string,
	facilities: PropTypes.arrayOf(PropTypes.object),
	baseUrl: PropTypes.string,
	faqContent: PropTypes.arrayOf(PropTypes.object),
	faqShowing: PropTypes.bool,
	listingsPerPage: PropTypes.number,
	loading: PropTypes.bool,
	meta: PropTypes.shape(metaPropTypes),
	page: PropTypes.number,
	pageType: PropTypes.string,
	phone: phonePropType,
	searchContent: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
	searchLocation: PropTypes.object,
	searchParams: PropTypes.object,
	searchType: PropTypes.string,
	showInformedSearch: PropTypes.bool,
	showRecommendedUnit: PropTypes.bool,
	totalListings: PropTypes.number,
	totalPages: PropTypes.number,
	trackingPixel: PropTypes.string,
	handleSearch: PropTypes.func,
	facetCounts: PropTypes.object,
	storageTypeUrls: PropTypes.shape({
		storage: PropTypes.string,
		vehicle: PropTypes.string,
	}),
	stateUrl: PropTypes.string,
	cityUrl: PropTypes.string,
	searchId: PropTypes.string,
	sf16978Variation: PropTypes.string,
	withinCallCenterHours: PropTypes.bool,
	staticMapUrl: PropTypes.string,
	cloudfrontUrl: PropTypes.string,
	useNewSearch: PropTypes.bool,
	statusCode: PropTypes.number,
	headline: PropTypes.string,
	viewportWidth: PropTypes.number,
};

export default compose(
	withExperiment(
		'sf_15810_ssdc_sticky_click_to_call_revisit',
		(props) =>
			props?.withinCallCenterHours &&
			props?.deviceType &&
			props.deviceType !== 'desktop',
	),
	withExperiment('sf_16978_ssdc_clp_redesign'),
)(NewSearchPage);
